import React from 'react';
import PropTypes from 'prop-types';
import CustomDataReadOnlyField from '../CustomDataReadOnlyField/CustomDataReadOnlyField';
import {
  CustomDataBlockHeader,
  CustomDataGroupHeader,
  CustomDataLeafElement
} from './YPCustomDataElement.style';
import Sticky from '../../styled components/Sticky';

/**
 * Renders a Yesplan Custom Data Element; groups, blocks and leaf elements.
 * Group and block levels are rendered with a sticky header, such that scrolling through the list of its custom data
 * always shows the group > block path.
 *
 * All type of leaf elements are rendered as a read-only representation of its label, value and comment.
 *
 * References to contact objects and contact links need to be passed in the `contactReferences` and `contactLinkReferences` to properly
 * display custom data of type _Contact_.
 */
class YPCustomDataElement extends React.Component {
  // eslint-disable-next-line no-unused-vars
  static stringForEmptyValue(type) {
    const noneTypes = ['Attachment', 'Labels', 'Integration info'];
    if (noneTypes.includes(type)) return `(${i18n('none')})`;
    return `(${i18n('not filled out')})`;
  }

  constructor(props) {
    super(props);

    this.state = {
      topGroupHeaderHeight: 0
    };

    this.topGroupHeaderRef = React.createRef();
  }

  componentDidMount() {
    this.updateTopGroupHeaderHeight();
  }

  componentDidUpdate() {
    this.updateTopGroupHeaderHeight();
  }

  updateTopGroupHeaderHeight() {
    const { topGroupHeaderHeight } = this.state;
    if (this.topGroupHeaderRef.current && topGroupHeaderHeight === 0) {
      const height = this.topGroupHeaderRef.current.clientHeight;
      /* Suppress the linting rule: it is allowed to call setState in componentDidMount (see React docs)
         and necessary here to ensure that the rendering uses the correct height of the element.
         This element is only available after mounting */
      /* eslint-disable-next-line  react/no-did-mount-set-state */
      if (height !== 0 && height !== topGroupHeaderHeight)
        this.setState({
          topGroupHeaderHeight: this.topGroupHeaderRef.current.clientHeight
        });
    }
  }

  isCustomDataGroup(element) {
    return element.childrenPerTab;
  }

  filterOutEmptyCustomData(collection) {
    return collection.filter((customData) => {
      if (this.isCustomDataGroup(customData)) {
        return this.filterOutEmptyCustomData(customData.childrenPerTab).length > 0;
      }
      return !customData.hasEmptyValue();
    });
  }

  renderCustomDataLeafElement(element) {
    return (
      <CustomDataReadOnlyField
        key={element.keyword}
        label={element.name}
        type={element.getTypeForRenderingAsReadOnly()}
        value={element.formatValue()}
        emptyValue={element.getEmptyValue()}
        description={element.comment || null}
      />
    );
  }

  renderCustomDataGroupLevel(group, level) {
    const { topPosition, showEmptyCustomData } = this.props;
    let customData = group.childrenPerTab;
    if (!showEmptyCustomData) customData = this.filterOutEmptyCustomData(customData);
    return (
      <div key={group.keyword} data-test-cdtype="customDataGroup">
        <Sticky $zIndex={2} $top={topPosition}>
          <CustomDataGroupHeader label={group.name} ref={this.topGroupHeaderRef} />
        </Sticky>
        {customData.map((child, index) => {
          const canPeek = index + 1 !== group.childrenPerTab.length;
          const peek = canPeek && group.childrenPerTab[index + 1];
          if (canPeek && !this.isCustomDataGroup(child) && this.isCustomDataGroup(peek)) {
            return (
              <React.Fragment key={`wrapper${child.keyword}`}>
                {this.renderCustomData(child, level + 1)}
              </React.Fragment>
            );
          }
          return this.renderCustomData(child, level + 1);
        })}
      </div>
    );
  }

  renderCustomDataBlockLevel(group, level) {
    const { topPosition, showEmptyCustomData } = this.props;
    const { topGroupHeaderHeight } = this.state;
    let customData = group.childrenPerTab;
    if (!showEmptyCustomData) customData = this.filterOutEmptyCustomData(customData);
    return (
      <div
        key={group.keyword}
        data-test-type="customData"
        data-test-cdtype="customDataBlock"
      >
        <Sticky $top={topPosition + topGroupHeaderHeight - 1} $zIndex={1}>
          <CustomDataBlockHeader label={group.name} />
        </Sticky>
        {customData.map((child) => this.renderCustomData(child, level + 1))}
      </div>
    );
  }

  renderCustomDataGroup(group, level) {
    if (level === 0) return this.renderCustomDataGroupLevel(group, level);
    return this.renderCustomDataBlockLevel(group, level);
  }

  renderCustomData(customData, level) {
    const { showEmptyCustomData } = this.props;
    if (!customData) return null;
    if (this.isCustomDataGroup(customData)) {
      if (
        !showEmptyCustomData &&
        this.filterOutEmptyCustomData([customData]).length === 0
      )
        return null;
      return this.renderCustomDataGroup(customData, level);
    }
    /* Leaf element */
    if (customData.type) {
      return (
        <CustomDataLeafElement
          key={customData.keyword}
          data-test-type="customData"
          data-test-cdtype={customData.type}
          $level={level}
        >
          {this.renderCustomDataLeafElement(customData)}
        </CustomDataLeafElement>
      );
    }
    return null;
  }

  render() {
    const { customData } = this.props;
    return this.renderCustomData(customData, 0);
  }
}

YPCustomDataElement.propTypes = {
  customData: PropTypes.shape({}).isRequired,
  topPosition: PropTypes.number,
  showEmptyCustomData: PropTypes.bool.isRequired
};

YPCustomDataElement.defaultProps = {
  topPosition: 0
};

export default YPCustomDataElement;
